/**
 * Terms of Service (#126, FR-13.1). Static, unauthenticated, prerenderable copy.
 * The product name comes from {@link BRAND_NAME} (config token, never hard-coded); the
 * binding entity + contact come from the shared legal constants via {@link LegalPage}.
 * Standard SaaS/consumer boilerplate; the pre-launch draft marker was dropped at
 * launch finalization (#135).
 */

import { LegalPage } from '../ui/legal/LegalPage';
import { BRAND_NAME } from '../ui/brand';

export function TermsPage(): React.JSX.Element {
  return (
    <LegalPage title="Terms of Service" testId="legal-terms" path="/terms">
      <section>
        <h2>1. Acceptance of these terms</h2>
        <p>
          By creating an account, purchasing credits, or otherwise using {BRAND_NAME} (the
          &ldquo;Service&rdquo;), you agree to these Terms of Service. If you do not
          agree, do not use the Service.
        </p>
      </section>
      <section>
        <h2>2. The Service</h2>
        <p>
          {BRAND_NAME} is an online, real-time trivia game platform. We may add, change,
          or remove features at any time. We aim for high availability but do not
          guarantee the Service will be uninterrupted or error-free.
        </p>
      </section>
      <section>
        <h2>3. Accounts</h2>
        <p>
          You may play as a guest or register an account. You are responsible for activity
          under your account and for keeping your credentials secure. You must provide
          accurate information and be old enough to form a binding contract in your
          jurisdiction.
        </p>
      </section>
      <section>
        <h2>4. Acceptable use</h2>
        <p>
          You agree not to abuse, disrupt, reverse-engineer, or overload the Service,
          upload unlawful or infringing content, or attempt to access other users&rsquo;
          data. We may suspend or terminate accounts that violate these terms.
        </p>
      </section>
      <section>
        <h2>5. Credits and payments</h2>
        <p>
          Paid features are accessed through <strong>credits</strong>, a prepaid digital
          good. Purchases are handled by our payment processor; by purchasing you also
          agree to the <a href="/refunds">Refund Policy</a>, which forms part of these
          terms. Credits have no cash value and are non-transferable.
        </p>
      </section>
      <section>
        <h2>6. Disclaimers and limitation of liability</h2>
        <p>
          The Service is provided &ldquo;as is&rdquo; without warranties of any kind to
          the fullest extent permitted by law. To the maximum extent permitted by law, our
          aggregate liability arising out of or relating to the Service is limited to the
          amount you paid us in the twelve months before the claim.
        </p>
      </section>
      <section>
        <h2>7. Changes to these terms</h2>
        <p>
          We may update these terms from time to time. Material changes are indicated by a
          new version identifier shown above. Continued use after a change means you
          accept the updated terms; where required, we will ask you to re-accept.
        </p>
      </section>
      <section>
        <h2>8. Governing law</h2>
        <p>
          These terms are governed by the laws of the operating entity&rsquo;s
          jurisdiction, excluding its conflict-of-law rules.
        </p>
      </section>
    </LegalPage>
  );
}
